import React, { Component } from 'react';
import PropTypes from 'prop-types';

import find from 'lodash/find';
import throttle from 'lodash/throttle';

import { SuiAutocompleteInput } from '@splashthat/sui';

import { loadPlacesLibrary, getPlaceDetailsByPlaceId, getPlaceSuggestions } from './lib';

const noop = () => {};

export default class LocationSearchInput extends Component {
    static propTypes = {
        value: PropTypes.string,
        defaultValue: PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        }),
        autocompleteSuggestions: PropTypes.array,
        venueObject: PropTypes.shape({
            address: PropTypes.string,
            city: PropTypes.string,
            country: PropTypes.string,
            name: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
            lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        /** Default is to use window.google, otherwise create window.google with this key or the one found in bootstrap data */
        googleMapsApiKey: PropTypes.string,

        /** Handler executed when input value changes. function(value) */
        onInputChange: PropTypes.func,

        /** Handler executed when place details are loaded when picking autocomplete suggestion */
        onVenueObjectChange: PropTypes.func,

        /** Handler executed when autocomplete suggestions change */
        onAutocompleteSuggestionsChange: PropTypes.func,

        /** Handler executed immediately when a selection is made from the autocomplete menu */
        onVenueSelect: PropTypes.func,
    };

    static defaultProps = {
        value: null,
        defaultValue: undefined,
        autocompleteSuggestions: null,
        venueObject: {
            address: '',
            city: '',
            country: '',
            lat: '',
            lng: '',
            name: '',
            state: '',
            zipCode: '',
        },
        googleMapsApiKey: undefined,
        onInputChange: noop,
        onVenueObjectChange: noop,
        onAutocompleteSuggestionsChange: noop,
        onVenueSelect: noop,
    };

    state = {
        value: '',
        autocompleteSuggestions: [],
    };

    componentDidMount() {
        loadPlacesLibrary(this.props.googleMapsApiKey);
    }

    handleInputChange = (event, value = '') => {
        const autocompleteSuggestion = this.findAutocompleteSuggestionFromDescription(value);
        this.setCurrentSuggestion(autocompleteSuggestion);
        if (value.length) {
            this.getAutocompleteSuggestions(value);
        } else {
            this.clearAutocompleteSuggestions();
        }
    };

    getAutocompleteSuggestions = throttle((input) => {
        getPlaceSuggestions(input, (results) => {
            if (results && Array.isArray(results)) {
                this.setAutocompleteSuggestions(results);
            } else {
                this.clearAutocompleteSuggestions();
            }
        });
    }, 100);

    clearAutocompleteSuggestions() {
        this.setAutocompleteSuggestions();
    }

    setAutocompleteSuggestions(autocompleteSuggestions = []) {
        if (this.props.autocompleteSuggestions == null) {
            this.setState({ autocompleteSuggestions });
        }

        this.props.onAutocompleteSuggestionsChange(autocompleteSuggestions);
    }

    getAutocompleteItems() {
        if (this.props.autocompleteSuggestions == null) {
            return this.state.autocompleteSuggestions;
        } else {
            return this.props.autocompleteSuggestions;
        }
    }

    updateVenueObject(placeId) {
        this.props.onVenueSelect();

        getPlaceDetailsByPlaceId(placeId).then((results) => {
            this.props.onVenueObjectChange(results || {});
        });
    }

    getVenueObject() {
        return this.props.venueObject;
    }

    getCurrentSuggestion() {
        const { value } = this.props;

        if (value == null) {
            return this.state.value;
        } else {
            return this.findAutocompleteSuggestionFromDescription(value);
        }
    }

    findAutocompleteSuggestionFromDescription(description) {
        const selectedAutocompleteItem = find(this.getAutocompleteItems(), {
            description,
        });

        return selectedAutocompleteItem || { label: description, value: null };
    }

    setCurrentSuggestion(value) {
        if (this.props.value == null) {
            this.setState({ value });
        }

        this.props.onInputChange(value.label);
    }

    handleAutocompleteSuggestionSelection = (event, suggestion) => {
        this.setCurrentSuggestion(suggestion);

        if (suggestion && suggestion.value) {
            this.updateVenueObject(suggestion.value);
        }
    };

    mapPlaceSuggestionForAutocomplete(place) {
        return !place
            ? null
            : {
                  label: place.description,
                  value: place.place_id,
                  ...place,
              };
    }

    render() {
        const autocompleteSuggestions = this.getAutocompleteItems();
        const currentSuggestion = this.getCurrentSuggestion();
        return (
            <SuiAutocompleteInput
                {...this.props}
                suggestionValues={autocompleteSuggestions.map(this.mapPlaceSuggestionForAutocomplete)}
                value={this.mapPlaceSuggestionForAutocomplete(currentSuggestion)}
                onInputChange={this.handleInputChange}
                onSuggestionSelect={this.handleAutocompleteSuggestionSelection}
                blurOnSelection
                overrideSuggestionFilter
            />
        );
    }
}
